import { computed, inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { EEOActions, EEOCategoriesSelectors } from '@core/eeo-data';
import { I18nService } from '@core/i18n-legacy';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import type { ManageEeoDialogResult } from './manage-eeo-dialog/manage-eeo-dialog.component';
import { ManageEeoDialogComponent } from './manage-eeo-dialog/manage-eeo-dialog.component';

@Injectable()
export class ViewsManageEeoBulkService {
  private readonly dialog = inject(DialogService);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);

  private readonly confirmDialogTexts = {
    enable: {
      title: this.i18n.translate('app.offers.manage_eeo.confirm_enable.title'),
      message: this.i18n.translate('app.offers.manage_eeo.confirm_enable.message'),
    },
    disable: {
      title: this.i18n.translate('app.offers.manage_eeo.confirm_disable.title'),
      message: this.i18n.translate('app.offers.manage_eeo.confirm_disable.message'),
    },
  };

  private eeoData = this.store.selectSignal(EEOCategoriesSelectors.data);
  private jobCategories = computed(() => this.eeoData()?.jobCategories || []);

  public manageEeo$({ ids }: { ids: number[] }): Observable<boolean> {
    return this.openManageDialog$({ ids }).pipe(
      switchMap(result => {
        if (!result) return of(null);
        return this.confirm$({ enable: result.action === 'enable', jobsCount: ids.length }).pipe(
          map(confirmResult => {
            if (!confirmResult) return null;
            return result;
          }),
        );
      }),
      tap(result => {
        if (!result) return;
        if (result.action === 'enable')
          this.store.dispatch(
            EEOActions.bulkEnable({
              payload: { offerIds: ids, jobCategory: result.jobCategory! },
            }),
          );
        else
          this.store.dispatch(
            EEOActions.bulkDisable({
              payload: { offerIds: ids },
            }),
          );
      }),
      switchMap(result => of(!!result)),
    );
  }

  private openManageDialog$({ ids }: { ids: number[] }): Observable<ManageEeoDialogResult> {
    return this.dialog.openCustom(
      ManageEeoDialogComponent,
      {
        width: DialogWidth.Small,
      },
      {
        jobCategories: this.jobCategories(),
        jobsCount: ids.length,
      },
    ).afterClosed;
  }

  private confirm$(props: { enable: boolean; jobsCount: number }): Observable<boolean> {
    const { enable, jobsCount } = props;
    const texts = enable ? this.confirmDialogTexts.enable : this.confirmDialogTexts.disable;
    const confirmConfig = {
      ...texts,
      agreeLabel: this.i18n.translate(
        enable
          ? 'app.offers.manage_eeo.confirm_enable.agree_label'
          : 'app.offers.manage_eeo.confirm_disable.agree_label',
        { count: jobsCount },
      ),
    };
    return enable ? this.dialog.information(confirmConfig) : this.dialog.warning(confirmConfig);
  }
}
